import { Component, DestroyRef, effect, inject, input, linkedSignal, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { v7 as uuidv7 } from 'uuid';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LabelRequiredDirective } from '@shared/directives/label-required.directive';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { toast } from 'ngx-sonner';
import { Cartographie } from '../../../../../cartographie/models/cartographie.model';
import { SidepanelService } from '../../../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../../../shared/components/toastr/toastr-success.component';
import { Phase } from '../../../processus-phases/models/phase.model';
import { PhaseStore } from '../../../processus-phases/store/phase.store';
import { Aptitude } from '../../models/aptitude.model';
import { AptitudeUpdateRequest } from '../../requests/update-request';
import { AptitudeStore } from '../../store/aptitude.store';
import { Référentiel } from '../../../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-form-aptitude',
  imports: [
    ReactiveFormsModule,
    HlmInputImports,
    HlmFieldImports,
    HlmSelectImports,
    BrnSelectImports,
    HlmInputGroupImports,
    LabelRequiredDirective,
  ],
  templateUrl: './form-aptitude.component.html',
})
export class FormAptitudeComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly #aptitudeStore = inject(AptitudeStore);
  readonly #phaseStore = inject(PhaseStore);
  readonly #destroyRef = inject(DestroyRef);
  readonly #sidepanelService = inject(SidepanelService);
  readonly phases = linkedSignal<Phase[]>(() => this.#phaseStore.entities());
  readonly phasesFiltrees = linkedSignal<Phase[]>(() => this.phases());
  readonly aptitude = input.required<Aptitude>();
  readonly referentiel = input.required<Référentiel>();
  readonly cartographie = input.required<Cartographie>();

  readonly form = this.#fb.group({
    nom: [null, Validators.required],
    phase: [null, Validators.required],
  });

  constructor() {
    effect(() => {
      this.nom.setValue(this.aptitude().nom);
      this.phase.setValue(this.#phaseStore.entityMap()[this.aptitude().idPhase]);
    });

    effect(() =>
      this.#aptitudeStore.charger({ idCartographie: this.cartographie().id, idRéférentiel: this.referentiel().id }),
    );
  }

  ngOnInit(): void {
    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
  }

  onSubmit() {
    if (this.nom.hasError('required')) {
      return;
    }
    const toastrId = uuidv7();

    const data: AptitudeUpdateRequest = {
      idReferentiel: this.aptitude().idReferentiel!,
      nom: this.nom.value,
      indice: this.aptitude().indice,
      idPhase: this.phase.value.id,
      id: this.aptitude().id,
    };

    this.#aptitudeStore.modifier({
      idAptitude: data.id,
      data,
      next: () => {
        toast.custom(ToastrSuccessComponent, {
          componentProps: { message: "Modification de l'aptitude réussie", id: toastrId },
          id: toastrId,
        });
        this.#sidepanelService.close();
        this.#phaseStore.charger(this.aptitude().idReferentiel!);
      },
      error: () => this.alreadyExists(data),
    });
  }

  alreadyExists(aptitudeAModifier: AptitudeUpdateRequest) {
    const aptitudes = this.#aptitudeStore.entities();

    const doublonsAvecAptitudesExistantes = aptitudes.filter(
      (x) =>
        aptitudeAModifier.nom === x.nom && aptitudeAModifier.idPhase === x.idPhase && aptitudeAModifier.id !== x.id,
    );

    if (doublonsAvecAptitudesExistantes.length > 0) {
      this.nom.setErrors({ notUnique: true });
    }
  }

  get nom(): AbstractControl {
    return this.form.get('nom') as AbstractControl;
  }

  get phase(): AbstractControl {
    return this.form.get('phase') as AbstractControl;
  }

  get nomPhase(): AbstractControl {
    return this.form.get('nomPhase') as AbstractControl;
  }
}
